'use client';

import { useConnectSection } from '@/lib/hooks/useSiteData';
import Link from 'next/link';
import { useParams } from 'next/navigation';

const ConnectSection = () => {
  const params = useParams();
  const currentLang = (params?.lang as string) || 'en';
  const { data: connectData, loading, error } = useConnectSection();

  // Helper to slugify text for URLs
  const slugify = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Helper to get link or generate default
  const getLinkOrDefault = (link: string | null | undefined, label: string) => {
    if (link && link !== '' && link !== '#' && link !== '/') {
      return link;
    }
    return `/${currentLang}/page/${slugify(label)}`;
  };

  // Helper function to decode base64 content
  const decodeContent = (base64Content: string) => {
    try {
      return atob(base64Content);
    } catch (error) {
      console.error('Error decoding base64 content:', error);
      return '';
    }
  };

  // Helper function to extract text from HTML content
  const extractTextFromHtml = (htmlContent: string) => {
    // Remove HTML tags and extract text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Show loading state
  if (loading) {
    return (
      <section className="connect-sec">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="story-img">
                <div className="placeholder" style={{ width: '100%', height: '400px', borderRadius: '8px' }}></div>
                <div className="story">
                  <div className="placeholder-glow">
                    <h4 className="placeholder col-8 mb-2"></h4>
                    <p className="placeholder col-12"></p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="connect-box">
                <div className="head mb-lg-4">
                  <div className="placeholder-glow">
                    <h2 className="placeholder col-6"></h2>
                  </div>
                </div>
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="service-box">
                    <h5 className="placeholder col-8 mb-2"></h5>
                    <div className="card-btn">
                      <div className="placeholder" style={{ width: '40px', height: '40px', borderRadius: '50%' }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className="connect-sec">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="story-img">
                <img src="/images/boxes/story_img.jpg" alt="Share your story" />
                <div className="story">
                  <h4>Share Your Story</h4>
                  <p>
                    If we've helped you connect with helpful resources and support, please share your story.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="connect-box">
                <div className="head mb-lg-4">
                  <h2>Stay <span>Connected</span></h2>
                </div>
                <div className="alert alert-warning" role="alert">
                  Unable to load services. Please try again later.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Process API data
  const storyTitle = connectData?.data?.title || 'Share Your Story';
  const storyContent = connectData?.data?.content || '';
  const connectTitle = connectData?.data?.specifications?.find((spec: any) => spec.key === 'title')?.value || 'Stay Connected';
  const storyImage = connectData?.data?.specifications?.find((spec: any) => spec.key === 'image')?.value || '/images/boxes/story_img.jpg';
  const services = connectData?.data?.specifications?.find((spec: any) => spec.key === 'links')?.value || [];

  // Decode and extract text from HTML content
  const decodedContent = decodeContent(storyContent);
  const storyText = extractTextFromHtml(decodedContent);

  // Fallback static data if API fails
  const fallbackServices = [
    { label: "Ask an Expert", link: "#" },
    { label: "Caregiver Tech Tool Finder", link: "#" },
    { label: "Sign Up for Our Newsletter", link: "#" },
    { label: "View Upcoming Events", link: "#" },
    { label: "Partner With Us", link: "#" },
    { label: "Donate to Support Our Mission", link: "#" }
  ];

  const fallbackStoryText = "If we've helped you connect with helpful resources and support, please share your story.";

  const displayServices = services.length > 0 ? services : fallbackServices;
  const displayStoryText = storyText || fallbackStoryText;

  return (
    <section className="connect-sec">
      <div className="container">
        <div className="row">
          <div className="col-lg-6">
            <div className="story-img">
              <img 
                src={storyImage} 
                alt="Share your story"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/images/boxes/story_img.jpg";
                }}
              />
              <div className="story">
                <h4>{storyTitle}</h4>
                <p>{displayStoryText}</p>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="connect-box">
              <div className="head mb-lg-4">
                <h2>{connectTitle.split(' ').map((word: string, index: number) => 
                  index === 0 ? word : <span key={index}>{word}</span>
                )}</h2>
              </div>
              {displayServices.map((service: any, index: number) => (
                <div key={service.label || index} className="service-box">
                  <h5>{service.label}</h5>
                  <div className="card-btn">
                    <Link href={getLinkOrDefault(service.link, service.label)} className="btn">
                      <img src="/images/icons/black_icon.svg" className="arrow-icon" alt="" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConnectSection;
